import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useEditorBundle, useBundleRoot } from '@/hooks/use-editor-bundle';
import { useHeaderAction } from '@/lib/header-action-context';
import { useCapabilityActions } from '@/hooks/use-capability-actions';
import { useCapabilityUpdate } from '@/hooks/use-entity-mutations';
import { CapabilityDetailLayout, WORKING_COPY_ID } from '@/components/capability-detail/CapabilityDetailLayout';
import { CapabilityHeaderActions } from '@/components/capability-detail/CapabilityHeaderActions';
import { api } from '@/lib/api-client';
import { logger } from '@/lib/telemetry';
import { capabilityDisplayTitle } from '@/types';
import { PolicyRuleEditor } from '@/components/policy-editor/PolicyRuleEditor';
import { generateRego } from '@/components/policy-editor/utils/rego-generator';
import { parseRego } from '@/components/policy-editor/utils/rego-parser';
import type { PolicyDocument } from '@/types/policy-editor';
import { EMPTY_POLICY_DOCUMENT } from '@/types/policy-editor';
import type { DeferredOverviewDirtyPayload } from '@/components/capability-detail/CapabilityOverviewTab';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Pencil } from 'lucide-react';
import { toast } from 'sonner';

const LOG_PREFIX = 'policy-detail-page';
const HEADER_LAYER_ID = 'policy-page';

interface RevisionFile {
    path?: string;
    content_type?: string;
    content_text?: string | null;
    contentText?: string | null;
}

function extractRegoFromEntity(entity: Record<string, unknown>, revisionId?: string): string {
    const tryExtract = (candidate: Record<string, unknown>): string | null => {
        const files = (candidate.files as RevisionFile[] | undefined) ?? [];
        for (const file of files) {
            if (file.path?.endsWith('.rego')) {
                const text = file.content_text ?? file.contentText;
                if (text) return text.replace(/^\uFEFF/, '').trim();
            }
        }
        const typeConfig = candidate.type_config as Record<string, unknown> | undefined;
        if (typeConfig?.rules && typeof typeConfig.rules === 'string') {
            return typeConfig.rules;
        }
        const metadata = candidate.metadata as Record<string, unknown> | undefined;
        if (metadata?.rego_source && typeof metadata.rego_source === 'string') {
            return metadata.rego_source;
        }
        return null;
    };

    const workingRevision = entity.working_revision as Record<string, unknown> | null;
    const immutableRevisions = (entity.immutable_revisions as Record<string, unknown>[] | undefined) ?? [];

    if (revisionId && revisionId !== WORKING_COPY_ID) {
        const target = immutableRevisions.find((r) => (r as { id?: string }).id === revisionId);
        if (target) {
            const rego = tryExtract(target);
            if (rego) return rego;
        }
    }

    const candidates = [workingRevision, immutableRevisions[0], entity].filter(Boolean) as Record<string, unknown>[];
    for (const candidate of candidates) {
        const rego = tryExtract(candidate);
        if (rego) return rego;
    }

    return '';
}

interface CapabilityEntity {
    id: string;
    name: string;
    display_name?: string | null;
    app_key?: string;
    status?: string;
    working_revision?: {
        id?: string;
        version_string?: string;
        base_revision_id?: string;
    } | null;
    immutable_revisions?: {
        id?: string;
        version_string?: string;
        status?: string;
    }[];
}

export default function PolicyDetailPage() {
    const { appKey, name } = useParams();
    const queryClient = useQueryClient();
    const { data: bundle, isLoading, refetchWithoutCache } = useEditorBundle(appKey!, name!);
    const data = useBundleRoot(bundle);
    const { pushHeaderLayer, removeHeaderLayer } = useHeaderAction();
    const [selectedRevisionId, setSelectedRevisionId] = useState(WORKING_COPY_ID);
    const [policyDoc, setPolicyDoc] = useState<PolicyDocument>(EMPTY_POLICY_DOCUMENT);
    const [originalRego, setOriginalRego] = useState('');
    const [configDirty, setConfigDirty] = useState(false);
    const [overviewPatch, setOverviewPatch] = useState<Record<string, unknown>>({});
    const [isSaving, setIsSaving] = useState(false);
    const updateMutation = useCapabilityUpdate(appKey ?? '', name ?? '');

    const entity = (data ?? undefined) as unknown as CapabilityEntity | undefined;
    const isViewingPublished = selectedRevisionId !== WORKING_COPY_ID;
    const isArchived = (entity?.status ?? '').toLowerCase() === 'archived';

    // Parse Rego from the entity into visual IR when data changes
    useEffect(() => {
        if (!data) return;
        const regoSource = extractRegoFromEntity(data as Record<string, unknown>, selectedRevisionId);
        setOriginalRego(regoSource);
        if (regoSource) {
            try {
                const parsed = parseRego(regoSource);
                setPolicyDoc(parsed);
            } catch (err) {
                logger.error(`${LOG_PREFIX}.parseRego: failed`, { error: err });
                setPolicyDoc(EMPTY_POLICY_DOCUMENT);
            }
        } else {
            setPolicyDoc(EMPTY_POLICY_DOCUMENT);
        }
        setConfigDirty(false);
    }, [data, selectedRevisionId]);

    const handleDocumentChange = useCallback((doc: PolicyDocument) => {
        setPolicyDoc(doc);
        setConfigDirty(true);
    }, []);

    const handleOverviewDeferredDirtyChange = useCallback((payload: DeferredOverviewDirtyPayload) => {
        setOverviewPatch((prev) => {
            const next = payload.patch;
            if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
            return next;
        });
    }, []);

    const overviewDirty = Object.keys(overviewPatch).length > 0;
    const isDirty = overviewDirty || configDirty;

    const handleSave = useCallback(async () => {
        if (!appKey || !name || !isDirty) return;
        setIsSaving(true);
        try {
            if (configDirty) {
                const regoSource = generateRego(policyDoc);
                await api.post(
                    `/applications/${appKey}/capabilities/${encodeURIComponent(name)}/upsert`,
                    {
                        targetRevision: 'working',
                        type_config: {
                            implementation: {
                                kind: 'policy-rego',
                                entrypoint: 'rules.rego',
                            },
                        },
                        files: [
                            {
                                path: 'rules.rego',
                                content: regoSource,
                                contentType: 'text/plain',
                            },
                        ],
                    },
                );
                setConfigDirty(false);
                setOriginalRego(regoSource);
            }
            if (overviewDirty) {
                await updateMutation.mutateAsync(overviewPatch);
                setOverviewPatch({});
            }
            await refetchWithoutCache();
            queryClient.invalidateQueries({ queryKey: ['capability', appKey, name] });
            toast.success('Policy saved', { description: 'Policy rules saved to database.' });
            logger.info(`${LOG_PREFIX}.handleSave: policy saved`, { name });
        } catch (err) {
            logger.error(`${LOG_PREFIX}.handleSave: failed`, { error: err });
            toast.error('Failed to save policy', {
                description: 'An unexpected error occurred. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    }, [appKey, name, isDirty, configDirty, overviewDirty, policyDoc, overviewPatch, queryClient, refetchWithoutCache, updateMutation]);

    const actions = useCapabilityActions({
        appKey: appKey!,
        name: name!,
        typeLabel: 'Policy',
        data: data as Record<string, unknown> | null,
        refetch: refetchWithoutCache,
        customSave: handleSave,
        customIsDirty: isDirty,
        customIsSaving: isSaving,
    });

    const saveRef = useRef(actions.save);
    saveRef.current = actions.save;
    const publishRef = useRef(actions.publish);
    publishRef.current = actions.publish;
    const editRef = useRef(actions.edit);
    editRef.current = actions.edit;

    useEffect(() => {
        if (!data) return;
        if (isArchived) {
            removeHeaderLayer(HEADER_LAYER_ID);
            return;
        }
        pushHeaderLayer(HEADER_LAYER_ID, {
            action: (
                <CapabilityHeaderActions
                    typeLabel="Policy"
                    hasWorkingRevision={actions.hasWorkingRevision}
                    isViewingPublished={actions.isViewingPublished}
                    isArchived={isArchived}
                    selectedPublishedLabel={actions.selectedPublishedLabel}
                    isDirty={actions.isDirty}
                    isSaving={actions.isSaving}
                    isPublishing={actions.isPublishing}
                    onSave={() => void saveRef.current()}
                    onPublish={() => void publishRef.current()}
                    onEdit={() => editRef.current()}
                    onArchive={() => actions.openArchiveDialog()}
                    onSettings={() => actions.openSettings()}
                />
            ),
        });
        return () => removeHeaderLayer(HEADER_LAYER_ID);
    }, [
        data, isArchived, actions.hasWorkingRevision, actions.isViewingPublished,
        actions.selectedPublishedLabel, actions.isDirty, actions.isSaving,
        actions.isPublishing, pushHeaderLayer, removeHeaderLayer,
    ]);

    if (isLoading) return <div className="p-6">Loading policy...</div>;
    if (!data) return <div className="p-6">Policy not found</div>;

    const readOnly = isSaving || !actions.hasWorkingRevision || isViewingPublished;

    return (
        <>
            <CapabilityDetailLayout
                typeLabel="Policy"
                appKey={appKey}
                capabilityName={name}
                entityData={data as Record<string, unknown> | undefined}
                selectedRevisionId={selectedRevisionId}
                onRevisionChange={setSelectedRevisionId}
                onOverviewDeferredDirtyChange={handleOverviewDeferredDirtyChange}
                pendingOverviewPatch={overviewPatch}
                onSaveDeferred={() => void handleSave()}
                isOverviewDirty={isDirty}
                isSavingOverview={isSaving}
                overviewFormDisabled={readOnly}
            >
                <PolicyRuleEditor
                    document={policyDoc}
                    onChange={handleDocumentChange}
                    readOnly={readOnly}
                />
            </CapabilityDetailLayout>

            <AlertDialog open={actions.replaceDialogOpen} onOpenChange={actions.setReplaceDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Pencil size={18} className="text-amber-600" />
                            Replace working copy?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            A working copy already exists. Replacing it will discard any unsaved changes
                            and create a new working copy based on the selected published version.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={actions.handleConfirmReplace}>
                            Replace
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
